/**
 * AttributionCard — displays a single fix attribution.
 * Spec §4.4 + 03-attribution-card.html.
 *
 * NOT clickable in v1 (spec §8.6).
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Attribution = Record<string, any>;

interface Props {
  attribution: Attribution;
  mobile?: boolean;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDelta(n: number): string {
  if (n > 0) return `+${n.toLocaleString()}`;
  return n.toLocaleString();
}

function formatPct(n: number | null): string {
  if (n === null) return '';
  if (n > 0) return `+${n.toFixed(0)}%`;
  return `${n.toFixed(0)}%`;
}

export function AttributionCard({ attribution, mobile = false }: Props) {
  const isConfirmed = attribution.state === 'confirmed';
  const paddingClass = mobile ? 'p-[18px]' : 'p-6';

  return (
    <div
      className={`
        relative bg-white rounded-xl border transition-all duration-150
        ${paddingClass}
        ${isConfirmed
          ? 'border-emerald-200'
          : 'border-amber-200 border-dashed'
        }
      `}
    >
      {/* Confirmed accent bar */}
      {isConfirmed && (
        <div className="absolute left-0 top-5 bottom-5 w-[3px] bg-emerald-500 rounded-r-[2px]" />
      )}

      {/* Head */}
      <div className="flex items-start justify-between gap-4 mb-3.5">
        <div>
          <p className={`font-semibold text-slate-900 leading-snug ${mobile ? 'text-sm' : 'text-[15px]'}`}>
            {attribution.title}
          </p>
          <p className={`text-slate-400 mt-1 ${mobile ? 'text-[11px]' : 'text-xs'}`}>
            Deployed {formatDate(attribution.deployedAt)} · {attribution.confidenceLabel}
          </p>
        </div>
        <span
          className={`
            flex-shrink-0 rounded font-bold uppercase tracking-wide
            ${mobile ? 'text-[9px] px-[7px] py-1' : 'text-[10px] px-2 py-1'}
            ${isConfirmed
              ? 'bg-emerald-500 text-white'
              : 'border border-amber-300 text-amber-600'
            }
          `}
        >
          {isConfirmed ? 'Confirmed' : 'Early signal'}
        </span>
      </div>

      {/* Stats grid */}
      <div className={`grid grid-cols-2 gap-4 rounded-lg bg-slate-50 mb-3.5 ${mobile ? 'p-3.5' : 'p-4'}`}>
        <div>
          <p className={`uppercase tracking-wide text-slate-500 font-medium ${mobile ? 'text-[10px]' : 'text-[11px]'}`}>
            Visit lift
          </p>
          <p className={`font-semibold text-slate-900 mt-1 tabular-nums ${mobile ? 'text-[18px]' : 'text-[22px]'}`}>
            {formatDelta(attribution.visits?.delta ?? 0)}
            <span className={`font-semibold ml-1 ${isConfirmed ? 'text-emerald-600' : 'text-amber-600'} ${mobile ? 'text-[11px]' : 'text-[13px]'}`}>
              {formatPct(attribution.visits?.percent ?? null)}
            </span>
          </p>
          <p className={`text-slate-400 mt-0.5 ${mobile ? 'text-[10px]' : 'text-[11px]'}`}>
            vs {(attribution.visits?.baseline ?? 0).toLocaleString()} baseline
          </p>
        </div>
        <div>
          <p className={`uppercase tracking-wide text-slate-500 font-medium ${mobile ? 'text-[10px]' : 'text-[11px]'}`}>
            Citation Δ
          </p>
          {attribution.citation?.state === 'measured' ? (
            <>
              <p className={`font-semibold text-slate-900 mt-1 tabular-nums ${mobile ? 'text-[18px]' : 'text-[22px]'}`}>
                {attribution.citation.deltaPp > 0 ? '+' : ''}{attribution.citation.deltaPp.toFixed(1)}pp
              </p>
              <p className={`text-slate-400 mt-0.5 ${mobile ? 'text-[10px]' : 'text-[11px]'}`}>
                {attribution.citation.thenPercent.toFixed(0)}% → {attribution.citation.nowPercent.toFixed(0)}%
              </p>
            </>
          ) : attribution.citation?.state === 'pending' ? (
            <>
              <p className={`font-medium text-amber-600 mt-1 ${mobile ? 'text-[13px]' : 'text-sm'}`}>
                Pending
              </p>
              <p className={`text-slate-400 mt-0.5 ${mobile ? 'text-[10px]' : 'text-[11px]'}`}>
                Confirms {formatDate(attribution.citation.expectedAt)}
              </p>
            </>
          ) : (
            <p className={`font-medium text-slate-400 mt-1 ${mobile ? 'text-[13px]' : 'text-sm'}`}>
              —
            </p>
          )}
        </div>
      </div>

      {/* Narrative */}
      {attribution.narrative && (
        <p
          className={`
            font-serif italic text-slate-600 leading-snug pl-3
            border-l-2
            ${mobile ? 'text-[13px]' : 'text-sm'}
            ${isConfirmed ? 'border-emerald-200' : 'border-amber-200'}
          `}
        >
          {attribution.narrative}
        </p>
      )}

      {/* Footer */}
      <div className={`flex items-center justify-between border-t border-slate-100 ${mobile ? 'mt-3 pt-3 text-[11px]' : 'mt-3.5 pt-3.5 text-xs'} text-slate-400`}>
        <span>Pageviews: {(attribution.visits?.baseline ?? 0).toLocaleString()} → {((attribution.visits?.baseline ?? 0) + (attribution.visits?.delta ?? 0)).toLocaleString()}</span>
        {attribution.methodology && (
          <span title={attribution.methodology} className="cursor-help hover:text-emerald-600 transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
              <circle cx="8" cy="8" r="6.5" />
              <path d="M8 7v5M8 5h.01" strokeLinecap="round" />
            </svg>
          </span>
        )}
      </div>
    </div>
  );
}
